import type { Role, RoleAssignment } from '@adornis/abac/policies/SimpleRolePolicy.js';
import { guardDialog } from '@adornis/dialog/dialog-functions.js';
import type { SectionRendererSection } from '@adornis/entity-relationship-management/client/details/SectionRenderer.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import type { AdornisUser } from '@adornis/users/db/a-user.js';
import { subscribePlatformRoleAssignmentFor } from '@nsc/nexus-permissions/nexus-api/platform-gated-reads.js';
import { NexusRoleNames } from '@nsc/nexus-permissions/platform-rbac/nexus-permissions.js';
import { NEXUS_ADMIN_ROLE_NAME } from '@nsc/nexus-permissions/platform-rbac/platform-admin-permissions.js';
import { subscribeAllEntitiesRoles } from '@nsc/nexus-permissions/subscribe-all-roles.js';
import { normalizeSchedulingKey } from '@nsc/workflows-partner-scheduling/ticketing/partner-scheduling-patterns.js';
import { html, nothing } from 'lit';
import { subscribePartnerSchedulingClientStatus } from '../../api/partner-scheduling-workbook-subscriptions.js';
import {
  addPartnerClientKeyToScope,
  getPartnerClientKeysFromScope,
  removePartnerClientKeyFromScope,
} from '../../features/permissions/scope-helpers.js';

type PartnerClientOption = { clientKey: string; label: string };

const PartnerClientScopeEditor = stateful(({ user }: { user: AdornisUser<any> }) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const rolesQuery = useQuery(() => subscribeAllEntitiesRoles()({ _id: 1, roleName: 1 }), []);
  const assignmentQuery = useQuery<RoleAssignment | null>(
    () => subscribePlatformRoleAssignmentFor(user._id)({ _id: 1, roleIds: 1, scope: { contexts: 1, permission: 1 } }),
    [user._id, refreshKey],
  );
  const statusQuery = useQuery(
    () => subscribePartnerSchedulingClientStatus()({ clientKey: 1, clientName: 1 }),
    [],
  );

  const roles = (rolesQuery.value ?? []) as Role[];
  const assignment = assignmentQuery.value;
  const editorRole = roles.find(r => r.roleName === NexusRoleNames.NEXUS_CLIENT_EDITOR);
  const adminRoleId = roles.find(r => r.roleName === NEXUS_ADMIN_ROLE_NAME)?._id;

  if (rolesQuery.loading || assignmentQuery.loading) {
    return html`<span class="loading loading-spinner loading-sm text-primary"></span>`;
  }

  if (adminRoleId && assignment?.roleIds?.includes(adminRoleId)) {
    return html`<p class="text-sm text-base-content/50">Admin role can toggle all partner-scheduling clients.</p>`;
  }

  if (!editorRole || !assignment?.roleIds?.includes(editorRole._id)) {
    return html`<p class="text-sm text-base-content/50">
      Assign Nexus Editor to scope partner-scheduling clients.
    </p>`;
  }

  const options: PartnerClientOption[] = (statusQuery.value ?? [])
    .map(row => {
      const clientKey = normalizeSchedulingKey(row.clientKey || row.clientName || '');
      if (!clientKey) return null;
      return { clientKey, label: row.clientName?.trim() || clientKey };
    })
    .filter((row): row is PartnerClientOption => row != null)
    .sort((a, b) => a.label.localeCompare(b.label));

  const optionByKey = new Map(options.map(o => [o.clientKey, o]));
  const assignedKeys = getPartnerClientKeysFromScope(assignment.scope ?? undefined);
  // Keep keys that left the workbook so admins can still remove stale scopes.
  const selected: PartnerClientOption[] = assignedKeys.map(
    key => optionByKey.get(key) ?? { clientKey: key, label: key },
  );
  const assignedKeySet = new Set(assignedKeys);
  const available = options.filter(o => !assignedKeySet.has(o.clientKey));
  const refresh = () => setRefreshKey(refreshKey + 1);

  const handleAdd = async (option: PartnerClientOption) => {
    await guardDialog(
      async () => {
        assignment.scope = addPartnerClientKeyToScope(assignment.scope ?? undefined, option.clientKey);
        await assignment.save?.();
      },
      {
        loadingText: 'Adding partner client…',
        successText: 'Partner client added to scope.',
        errorMessagePrefix: 'Could not add partner client',
      },
    );
    refresh();
  };

  const handleRemove = async (clientKey: string) => {
    await guardDialog(
      async () => {
        assignment.scope = removePartnerClientKeyFromScope(assignment.scope ?? undefined, clientKey);
        await assignment.save?.();
      },
      {
        loadingText: 'Removing partner client…',
        successText: 'Partner client removed from scope.',
        errorMessagePrefix: 'Could not remove partner client',
      },
    );
    refresh();
  };

  return html`
    <div class="flex flex-col gap-2">
      <p class="text-sm text-base-content/60">
        Partner-scheduling clients from the workbook (separate from Nexus system clients).
      </p>
      <div class="flex flex-wrap gap-2">
        ${selected.map(
          o =>
            html`<span class="badge badge-outline inline-flex items-center gap-1"
              >${o.label}<button
                type="button"
                class="btn btn-ghost btn-xs px-0"
                @click=${() => handleRemove(o.clientKey)}
              >
                ×
              </button></span
            >`,
        )}
      </div>
      ${available.length
        ? html`<div class="max-w-md w-full min-w-0">
            ${DropdownSelectionInput<PartnerClientOption, false>({
              multi: false,
              placeholder: 'Add partner client to scope…',
              selectables: available,
              value: null,
              renderString: o => o.label,
              resolveID: o => o.clientKey,
              emptyState: 'No partner clients',
              onPicked: v => {
                if (v) void handleAdd(v);
              },
            })}
          </div>`
        : options.length === 0
          ? html`<p class="text-sm text-base-content/50">No partner clients yet — import a workbook first.</p>`
          : nothing}
    </div>
  `;
});

export const partnerSchedulingClientAssignmentSection: SectionRendererSection<AdornisUser> = {
  title: 'Partner scheduling client scope',
  fields: [{ render: ({ entity: user }) => (!user ? nothing : PartnerClientScopeEditor({ user })) }],
};
